<template>
  <div
    :class="[
      'form-control',
      {
        'initial:pb-4': !error,
        'fc-error': !!error,
        'flex-row': isInline,
      },
    ]"
  >
    <span :class="labelWidth">
      <slot name="label">
        <m-label :theme="labelTheme" :for="props.for" :class="labelClass" v-if="label">
          {{ label }}
        </m-label>
      </slot>

      <m-more-info v-if="tooltip">
        {{ tooltip }}
      </m-more-info>
    </span>
    <slot name="topError" :error="error"></slot>
    <div
      :class="[
        'relative pl-1',
        {
          grow: isInline,
        },
      ]"
    >
      <slot />
    </div>
    <label v-if="bottomHintText && !error" class="label">
      <span class="label-text-alt">{{ bottomHintText }}</span>
    </label>

    <template v-if="!$slots.topError">
      <m-form-control-error v-if="!$slots.error" :error="error" class="mb-4"> </m-form-control-error>
      <slot v-else name="error" :error="error"></slot>
    </template>
  </div>
</template>

<script lang="ts" setup>
  import { computed } from 'vue';
  import MLabel from '../MLabel/MLabel.vue';
  import type { MLabelProps } from '../MLabel/MLabel.vue';
  import type { MFormControlErrorProps } from './MFormControlError.vue';
  import MFormControlError from './MFormControlError.vue';
  import MMoreInfo from '@shared/components/MMoreInfo/MMoreInfo.vue';

  defineSlots<{
    default?: () => any;
    /**
     * Override label
     */
    label?: () => any;
    /**
     * Display Error Message below label
     */
    topError?: (props: MFormControlErrorProps) => any;
    /**
     * Display Error Message below input, override default error message
     */
    error?: (props: MFormControlErrorProps) => any;
  }>();

  export interface MFormControlLabelProps extends MLabelProps {
    label?: string;
    for?: MLabelProps['for'];
    theme?: MLabelProps['theme'];
    class?: string;
  }

  export interface MFormControlProps {
    label?: string;
    for?: string;
    error?: MFormControlErrorProps['error'];
    bottomHintText?: string;
    labelTheme?: MLabelProps['theme'];
    inline?: boolean | string;
    labelClass?: string;
    tooltip?: string;
  }

  const props = withDefaults(defineProps<MFormControlProps>(), {
    label: undefined,
    labelTheme: 'default',
    for: undefined,
    error: undefined,
    bottomHintText: undefined,
    inline: false,
    labelClass: undefined,
  });

  const labelWidth = computed(() => {
    if (typeof props.inline === 'string') {
      return props.inline;
    }
    return '';
  });

  const isInline = computed(() => {
    return props.inline !== undefined && props.inline !== false;
  });
</script>
<style scoped>
  .fc-error :deep(:is(label, .text-error-content)) {
    @apply text-error;
  }

  .fc-error :deep(.input) {
    @apply text-error;
  }
</style>
